import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logo from '../img/logo-softmining.svg';
import Header from '../components/header';
import { processPdbId, processFile } from '../Utility/DownloadPDB/downloadPDBUtils';
import { retrieveMineData, consumeToken } from '../Utility/ConsumeToken/consumeToken';
import { FaArrowRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { navigateToNextPage } from "../Utility/CreatePipeline/createPipelineUtils";

const DownloadPdbComponent = () => {
  const [pdbId, setPdbId] = useState('');
  const [multiPdbInput, setMultiPdbInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState([]);
  const [mode, setMode] = useState('single'); // 'single' o 'file'
  const [showModal, setShowModal] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // --- parse & validate textarea (max 10 PDB IDs, uno per riga) ---
  const parseAndValidateTxt = (text) => {
    const linesRaw = text.split(/\r?\n/);
    const lines = linesRaw.map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return { ok: false, error: 'The input is empty.' };
    if (lines.length > 10) return { ok: false, error: 'You can insert at most 10 PDB IDs (one per line).' };

    const ids = [];
    for (let i = 0; i < lines.length; i++) {
      const tokens = lines[i].split(/\s+/).filter(Boolean);
      if (tokens.length !== 1) {
        return {
          ok: false,
          error: `Invalid format at line ${i + 1}: exactly one PDB ID per line is required.`,
        };
      }
      const id = tokens[0].toUpperCase();
      if (!/^[A-Z0-9]{4}$/.test(id)) {
        return {
          ok: false,
          error: `Invalid PDB ID at line ${i + 1}: "${tokens[0]}". Expected 4 alphanumeric characters.`,
        };
      }
      ids.push(id);
    }
    return { ok: true, ids };
  };

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const apitoken = await retrieveMineData();
      console.log(apitoken);
      if (apitoken.tokens > 1) {
        await consumeToken(-2);
        let newResults = [];

        if (mode === 'single') {
          const trimmedId = pdbId.trim().toUpperCase();
          const result = await processPdbId(apitoken, trimmedId);
          newResults = [result];
        } else if (mode === 'file') {
          if (!multiPdbInput.trim()) {
            throw new Error('Please insert at least one PDB ID.');
          }

          const res = parseAndValidateTxt(multiPdbInput);
          if (!res.ok) {
            throw new Error(res.error);
          }

          // Creiamo un "File" virtuale da passare a processFile,
          // riutilizzando la logica esistente lato utils/backend.
          const cleanTxt = res.ids.join('\n');
          const blob = new Blob([cleanTxt], { type: 'text/plain' });
          const virtualFile = new File([blob], 'pdb_ids.txt', { type: 'text/plain' });

          newResults = await processFile(virtualFile, apitoken);
        }

        setResults(newResults);
        setShowModal(true);
      } else {
        alert("Insufficient token");
      }
    } catch (error) {
      setError(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  // Funzione per procedere con la pipeline se presente
  const proceedPipeline = () => {
    if (!location.state) return;
    const { params = {}, board = [], nextPageIndex = 0 } = location.state;

    const newParams = { ...params, pdbResults: results };

    navigateToNextPage(nextPageIndex, newParams, board, navigate);
  };

  const isSubmitDisabled =
    loading ||
    (mode === 'single' && !pdbId.trim()) ||
    (mode === 'file' && !multiPdbInput.trim());

  return (
    <>
      <Header />
      <div className="riga headerTools">
        <h3 className="titolo titoloFeature">DownloadPDB</h3>
        <p className="text description">
          The tool retrieves structures from the RCSB Protein Data Bank (
          <a href="https://www.rcsb.org" target="_blank" rel="noreferrer">www.rcsb.org</a>).
          Users can enter a single PDB ID or provide multiple IDs (one per line, up to 10).
          The resulting PDB files are saved on the DownloadPDB Storage page.
        </p>
        <div className="space"></div>
      </div>
      <center>
        <div className="form formLogin formTools">
          <img src={logo} className="sign-logo" alt="logo" />

          {/* Selezione modalità */}
          <label className="selectLabel">Choose how many PDB IDs to send:</label>
          <div className="mode-selection">
            <button
              type="button"
              className={`mode-button ${mode === 'single' ? 'active' : ''}`}
              onClick={() => setMode('single')}
            >
              One PDB ID
            </button>
            <button
              type="button"
              className={`mode-button ${mode === 'file' ? 'active' : ''}`}
              onClick={() => setMode('file')}
            >
              Multiple PDB IDs
            </button>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            {mode === 'single' && (
              <>
                <div>
                  <label htmlFor="pdbId">PDB ID:</label>
                  <input
                    placeholder="Enter PDB ID..."
                    type="text"
                    id="pdbId"
                    value={pdbId}
                    onChange={(e) => setPdbId(e.target.value)}
                    required
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
              </>
            )}

            {mode === 'file' && (
              <>
                <div>
                  <label htmlFor="multiPdbInput">
                    Enter PDB IDs (one per line - max 10 lines):
                  </label>
                  <textarea
                    id="multiPdbInput"
                    rows={3}
                    placeholder={`e.g.\n1ABC\n2XYZ`}
                    value={multiPdbInput}
                    onChange={(e) => setMultiPdbInput(e.target.value)}
                    required
                    style={{ resize: 'vertical', textTransform: 'uppercase' }}
                  />
                </div>
              </>
            )}

            <button className="sign-button button" type="submit" disabled={isSubmitDisabled}>
              {loading ? 'Processing...' : 'Submit'}
            </button>

            {error && <p style={{ color: 'red' }}>Error: {error}</p>}
          </form>
          <div className="riga">
            <Link className="textlink textLinkSign" to="/dashboard">
              Back to Dashboard{' '}
              <FaArrowRight size={12} style={{ marginLeft: '8px', marginBottom: '-1px' }} />
            </Link>
          </div>
        </div>
        <div className="space"></div>
      </center>

      {/* Modal dei risultati */}
      {showModal && (
        <div className="popup-overlay">
          <div className="popup-content">
            <div className="divCloseButton">
              <button onClick={() => setShowModal(false)} className="CloseButton text">
                X
              </button>
            </div>
            <h4 className="popupTitle titolo">Results:</h4>
            <p className="resultText">
              {mode === 'single'
                ? "The resulting pdb file has been saved on the DownloadPDB Storage page."
                : "The resulting pdb files have been saved on the DownloadPDB Storage page."}
            </p>
            {mode === 'single' && (
              <ol>
                {results.map((result, index) => (
                  <label key={index} className="resultText">
                    <li>
                      <a
                        href={result.url}
                        download={result.name}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {result.name}
                      </a>
                    </li>
                  </label>
                ))}
              </ol>
            )}
            {location.state && location.state.nextPageIndex !== undefined ? (
              <button className="sign-button button" onClick={proceedPipeline}>
                Proceed to Next Pipeline Tool
              </button>
            ) : (
              <button
                className="sign-button button"
                onClick={() => navigate('/DownloadPDBStorage')}
              >
                Go to DownloadPDB Storage
              </button>
            )}
            <div className="riga">
              <Link
                className="textlink textLinkSign"
                to="/dashboard"
              >
                Back to Dashboard{' '}
                <FaArrowRight
                  size={12}
                  style={{ marginLeft: '8px', marginBottom: '-1px' }}
                />
              </Link>
            </div>
            
          </div>
        </div>
      )}
    </>
  );
};

export default DownloadPdbComponent;
